import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Globe, Trash2 } from 'lucide-react'
import { api } from '../../api/client'
import Button from '../../components/ui/Button'
import Input, { Textarea } from '../../components/ui/Input'

export default function ScraperPage() {
  const queryClient = useQueryClient()
  const [urls, setUrls] = useState('')
  const [category, setCategory] = useState('')
  const [tags, setTags] = useState('')

  const scrapeMutation = useMutation({
    mutationFn: (data: { urls: string[]; category?: string; tags?: string }) =>
      api.post<any>('/scraper/scrape', data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scraped'] }),
  })

  const { data: contentData } = useQuery({
    queryKey: ['scraped'],
    queryFn: () => api.get<{ contents: any[] }>('/scraper/content?limit=50'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/scraper/content/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['scraped'] }),
  })

  const handleScrape = (e: React.FormEvent) => {
    e.preventDefault()
    const urlList = urls.split(/[,\n]/).map(u => u.trim()).filter(Boolean)
    const payload: { urls: string[]; category?: string; tags?: string } = { urls: urlList }
    if (category) payload.category = category
    if (tags) payload.tags = tags
    scrapeMutation.mutate(payload)
  }

  const results = scrapeMutation.data?.results || scrapeMutation.data || []
  const library = contentData?.contents || []

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
          <Globe size={20} className="text-indigo-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Web Scraper</h1>
          <p className="text-sm text-gray-500">Extract structured data from websites for research and analysis</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleScrape} className="space-y-4">
            <Textarea
              label="URLs (comma or newline separated, max 20)"
              value={urls}
              onChange={(e) => setUrls(e.target.value)}
              required
              placeholder={'https://example.com\nhttps://another.com'}
            />
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. competitor"
              />
              <Input
                label="Tags"
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="Comma-separated"
              />
            </div>
            <Button type="submit" loading={scrapeMutation.isPending} className="w-full">
              <Globe size={16} /> Scrape
            </Button>
            {scrapeMutation.error && (
              <div className="p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                {(scrapeMutation.error as Error).message}
              </div>
            )}
          </form>
        </div>

        <div>
          {scrapeMutation.isPending && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center justify-center text-gray-400">
              <div className="w-8 h-8 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-sm">Scraping pages...</p>
            </div>
          )}
          {Array.isArray(results) && results.length > 0 && !scrapeMutation.isPending ? (
            <div className="space-y-3">
              {results.map((r: any, i: number) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="text-sm font-semibold text-gray-900">{r.title || r.url}</h3>
                  <p className="text-xs text-gray-500 truncate">{r.url}</p>
                  {r.meta_description && (
                    <p className="text-xs text-gray-600 mt-1">{r.meta_description}</p>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    {r.word_count != null && <span>{r.word_count} words</span>}
                    {r.quality_score != null && (
                      <span>Quality: {Math.round(r.quality_score * 100)}%</span>
                    )}
                    {r.keywords?.length > 0 && <span>{r.keywords.length} keywords</span>}
                  </div>
                </div>
              ))}
            </div>
          ) : !scrapeMutation.isPending ? (
            <div className="bg-white rounded-xl border border-gray-200 p-12 flex flex-col items-center text-gray-400">
              <Globe size={32} className="mb-3 opacity-30" />
              <p className="text-sm">Scraped content will appear here</p>
            </div>
          ) : null}
        </div>
      </div>

      {library.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Scraped Content Library</h2>
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-gray-50">
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">URL</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-gray-500 uppercase">Words</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {library.map((c: any) => (
                  <tr key={c.id}>
                    <td className="px-4 py-2 text-sm font-medium text-gray-900">{c.title || '\u2014'}</td>
                    <td className="px-4 py-2 text-xs text-gray-500 truncate max-w-xs">{c.url}</td>
                    <td className="px-4 py-2 text-sm text-gray-500">{c.word_count}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => { if (confirm('Delete this scraped content?')) deleteMutation.mutate(c.id) }}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
